import React, { useMemo, useState } from 'react';
import {
  Box,
  Menu,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import {
  RichTreeView,
  TreeItem,
  TreeItemProps,
  useTreeItemModel,
} from '@mui/x-tree-view';
import ArrowRightIcon from '@material-ui/icons/ArrowRight';
import ArrowDownIcon from '@material-ui/icons/ArrowDropDown';
import { getLocationDisplayValue, findTreeNodeLabel } from './treeUtils';
import { TreeNode } from './types';

const useStyles = makeStyles({
  menuPaper: {
    maxHeight: 320,
    width: 520,
    padding: 16,
  },
});

/**
 * Tree item that renders the node label with its aggregate count aligned to the
 * right. The composed node is passed via the standard `label` prop so no custom
 * slot typing is required.
 */
const LocationTreeItem = React.forwardRef<HTMLLIElement, TreeItemProps>(
  function LocationTreeItem(props, ref) {
    const item = useTreeItemModel<TreeNode>(props.itemId);

    return (
      <TreeItem
        {...props}
        ref={ref}
        label={
          <Box display="flex" justifyContent="space-between" width="100%">
            <span>{item?.label}</span>
            {item?.count !== undefined && (
              <Typography variant="body2" color="textSecondary">
                {item.count}
              </Typography>
            )}
          </Box>
        }
      />
    );
  },
);

interface TreeSelectFieldProps {
  label: string;
  placeholder: string;
  nodes: TreeNode[];
  value: string | string[];
  onChange: (value: string | string[]) => void;
  required?: boolean;
  multiple?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  showCounts?: boolean;
}

export function TreeSelectField({
  label,
  placeholder,
  nodes,
  value,
  onChange,
  required,
  multiple = false,
  disabled = false,
  error,
  helperText,
  showCounts = false,
}: TreeSelectFieldProps) {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);

  const displayValue = useMemo(() => {
    if (multiple) {
      return getLocationDisplayValue(nodes, value as string[], placeholder);
    }

    const selectedId = value as string;
    if (!selectedId) {
      return placeholder;
    }

    return findTreeNodeLabel(nodes, selectedId) ?? placeholder;
  }, [multiple, nodes, placeholder, value]);

  const selectedItems = multiple
    ? (value as string[])
    : (value as string) || null;

  const handleSingleSelect = (itemId: string) => {
    onChange(itemId);
    setAnchorEl(null);
  };

  return (
    <>
      <TextField
        label={label}
        required={required}
        variant="outlined"
        fullWidth
        disabled={disabled}
        value={displayValue}
        onClick={event => {
          if (disabled) return;
          setAnchorEl(event.currentTarget);
        }}
        InputProps={{
          readOnly: true,
          endAdornment: <ArrowDropDownIcon />,
        }}
        InputLabelProps={{ shrink: true }}
        error={error}
        helperText={helperText}
      />
      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={() => setAnchorEl(null)}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ className: classes.menuPaper }}
      >
        <RichTreeView
          items={nodes}
          checkboxSelection
          multiSelect={multiple}
          selectedItems={selectedItems}
          onSelectedItemsChange={(_, itemIds) => {
            if (multiple) {
              onChange(itemIds as string[]);
              return;
            }

            if (typeof itemIds === 'string' && itemIds) {
              handleSingleSelect(itemIds);
            }
          }}
          expansionTrigger="content"
          getItemLabel={(item: TreeNode) => item.label}
          slots={{
            ...(showCounts ? { item: LocationTreeItem } : {}),
            expandIcon: ArrowRightIcon,
            collapseIcon: ArrowDownIcon,
          }}
        />
      </Menu>
    </>
  );
}
